import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import RfqService from "../../Services/rfq_service";
import ViewOrderDetails from "./RFQ_ContentViewOrderDetails";

const Rfqcontent = () => {
  const navigate = useNavigate();

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Top section with Back button and RFQ button */}
      <div className="flex justify-between items-center mb-6">
        <button
          className="text-[#2b6e73] border border-[#2b6e73] rounded-full w-9 h-9 flex items-center justify-center"
          onClick={() => navigate("/customerPortal/MyDashboard")}
        >
          ←
        </button>
        <button
          className="bg-[#2b6e73] text-white px-4 py-2 rounded hover:bg-[#236e73] transition-colors "
          onClick={() => navigate("/get-quote")}
        >
          RFQ
        </button>
      </div>

      <h2 className="text-lg font-semibold text-[#2b6e73] mb-4">RFQ's</h2>
      <p className="text-gray-600 mb-6">Here’s the summary of RFQ's</p>
      <QuotationsTable />
    </div>
  );
};

export default Rfqcontent;

const QuotationsTable = () => {
  const [rfqs, setRfqs] = useState([]);
  const [filteredRfqs, setFilteredRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRfq, setSelectedRfq] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Set to 10 items per page
  const navigate = useNavigate();

  useEffect(() => {
    const loadRfqs = async () => {
      const token = localStorage.getItem("accessToken");
      const userName = localStorage.getItem("userName");

      if (!token || !userName) {
        setError("Please log in to view your RFQs.");
        setLoading(false);
        return;
      }

      try {
        const result = await RfqService.getAllRfqs({ userName, token, limit: 100 });
        if (result.success) {
          const flattenedRfqs = result.data.flatMap((rfq) =>
            rfq.products.map((product) => ({
              rfqNo: rfq.rfqNo,
              customerName: rfq.customerName,
              mobileNumber: rfq.mobileNumber,
              companyName: rfq.companyName,
              customerEmail:
                rfq.customerEmail ||
                `${rfq.customerName?.toLowerCase()}@gmail.com`,
              productName: product.productName,
              casNumber: product.casNumber || "N/A",
              quantity: product.quantity,
              catalogNumber: product.catalogNumber,
              status: "Requested",
              createdAt: rfq.createdAt,
              address: rfq.address || "N/A",
            }))
          );
          setRfqs(flattenedRfqs);
          setFilteredRfqs(flattenedRfqs);
        } else {
          setError(result.message || "Failed to fetch RFQs");
        }
      } catch (err) {
        setError("An error occurred while fetching RFQs");
      } finally {
        setLoading(false);
      }
    };

    loadRfqs();
  }, []);

  useEffect(() => {
    const filtered = rfqs.filter((rfq) => {
      const formattedDate = rfq.createdAt
        ? new Date(rfq.createdAt)
            .toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
            .replace(/ /g, " ")
            .toLowerCase()
        : "";
      const query = searchQuery.toLowerCase();
      return (
        rfq.rfqNo.toLowerCase().includes(query) ||
        rfq.productName.toLowerCase().includes(query) ||
        formattedDate.includes(query) ||
        (rfq.customerEmail &&
          rfq.customerEmail.toLowerCase().includes(query)) ||
        (rfq.casNumber && rfq.casNumber.toLowerCase().includes(query))
      );
    });
    setFilteredRfqs(filtered);
    setCurrentPage(1); // Reset to first page on search
  }, [searchQuery, rfqs]);

  const formatDate = (dateString) => {
    return new Date(dateString)
      .toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      .replace(/ /g, " ");
  };

  const handleView = (rfq) => {
    setSelectedRfq(rfq);
  };

  const closeView = () => {
    setSelectedRfq(null);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
  };

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredRfqs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredRfqs.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow mt-6 overflow-x-auto">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-[#2b6e73]">Recent Orders</h2>
        <div className="relative">
          <input
            type="text"
            placeholder="Enquiry ID, Product Name, Date (DD-MMM-YYYY), Email, or CAS No"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="p-2 pr-8 border border-gray-300 rounded-lg w-80"
          />
          {searchQuery && (
            <button
              onClick={handleClearSearch}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-red-600"
            >
              ✕
            </button>
          )}
        </div>
      </div>
      {loading ? (
        <div className="text-center text-gray-500">Loading...</div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : filteredRfqs.length === 0 ? (
        <div className="text-center text-gray-500">No Orders Available</div>
      ) : (
        <>
          <table className="min-w-full text-sm text-left border border-gray-300 rounded-lg">
            <thead className="bg-teal-700 text-white">
              <tr>
                <th className="p-2 text-center">Enquiry ID</th>
                <th className="p-2 text-center w-48">Company Name</th>
                <th className="p-2 text-center w-40">Assignee Name</th>
                <th className="p-2 text-center">Address</th>
                <th className="p-2 text-center w-32">Order Date</th>
                <th className="p-2 text-center">Status</th>
                <th className="p-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.map((rfq, index) => (
                <tr key={index} className="border-b hover:bg-gray-50">
                  <td className="p-2 text-center">{rfq.rfqNo || "N/A"}</td>
                  <td className="p-2 text-center">
                    {rfq.companyName || "N/A"}
                  </td>
                  <td className="p-2 text-center">
                    {rfq.customerName || "N/A"}
                  </td>
                  <td className="p-2 text-center">{rfq.address || "N/A"}</td>
                  <td className="p-2 text-center w-32">
                    {rfq.createdAt ? formatDate(rfq.createdAt) : "N/A"}
                  </td>
                  <td className="p-2 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        rfq.status === "Requested"
                          ? "bg-yellow-100 text-[#2b6e73] font-semibold"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {rfq.status === "Requested"
                        ? "Requested"
                        : rfq.status || "N/A"}
                    </span>
                  </td>
                  <td className="p-2 text-center space-x-2">
                    <button
                      className="text-teal-700 hover:underline text-sm font-semibold"
                      onClick={() => handleView(rfq)}
                    >
                      👁️ View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination Controls */}
          {/* Pagination Controls */}
{totalPages > 1 && (
  <div className="flex justify-between items-center mt-4">
    <div className="text-sm text-gray-600">
      Showing {indexOfFirstItem + 1} to{" "}
      {Math.min(indexOfLastItem, filteredRfqs.length)} of{" "}
      {filteredRfqs.length} entries
    </div>
    <div className="flex space-x-2">
      {/* First Button */}
      <button
        onClick={() => handlePageChange(1)}
        disabled={currentPage === 1}
        className={`px-3 py-1 rounded ${
          currentPage === 1
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-teal-700 text-white hover:bg-teal-800"
        }`}
      >
        « First
      </button>

      {/* Prev Button */}
      <button
        onClick={() => handlePageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-3 py-1 rounded ${
          currentPage === 1
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-teal-700 text-white hover:bg-teal-800"
        }`}
      >
        &lt;
      </button>

      {/* Page Numbers */}
      {[...Array(totalPages).keys()].map((number) => (
        <button
          key={number + 1}
          onClick={() => handlePageChange(number + 1)}
          className={`px-3 py-1 rounded ${
            currentPage === number + 1
              ? "bg-teal-700 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {number + 1}
        </button>
      ))}

      {/* Next Button */}
      <button
        onClick={() => handlePageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-3 py-1 rounded ${
          currentPage === totalPages
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-teal-700 text-white hover:bg-teal-800"
        }`}
      >
        &gt;
      </button>

      {/* Last Button */}
      <button
        onClick={() => handlePageChange(totalPages)}
        disabled={currentPage === totalPages}
        className={`px-3 py-1 rounded ${
          currentPage === totalPages
            ? "bg-gray-200 text-gray-500 cursor-not-allowed"
            : "bg-teal-700 text-white hover:bg-teal-800"
        }`}
      >
        Last »
      </button>
    </div>
  </div>
)}

          {selectedRfq && (
            <div className="mt-4">
              <ViewOrderDetails rfq={selectedRfq} closeView={closeView} />
            </div>
          )}
        </>
      )}
    </div>
  );
};